import { router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { useUpdateNonProfitOrg } from '@/api/hooks/nonProfitOrgHooks';
import NpoImpactStudyForm from '@/components/forms/NpoImpactStudyForm';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { ImpactStudyData } from '@/lib/validations/NpoOnboardingSchema';
import { containsSection } from '@/utils/containsSection';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { FileToUpload } from '@/utils/uploadMultipleToFireStorage';
import { uploadToFireStorage } from '@/utils/uploadToFireStorage';

const NpoImpactStudy = () => {
  const { impactStudy, impactStudyDeny } = useLocalSearchParams<{
    impactStudy: string;
    impactStudyDeny: string;
  }>();
  const { state: authState } = useAuth();
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();
  const { showSuccess, showError } = useEndFlowModal();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);

  const initialData = {
    impactStudy: typeof impactStudy === 'string' ? safeJsonParseArray(impactStudy)[0] : '',
    impactStudyDeny: typeof impactStudyDeny === 'string' ? impactStudyDeny === 'true' : impactStudyDeny
  };

  const updateNonProfit = ({
    impactStudy,
    impactStudyDeny
  }: {
    impactStudy: string[] | null;
    impactStudyDeny: boolean;
  }) => {
    nonProfitUpdate(
      {
        npoId: authState.signUpData?.loginEmail?.npoId ?? '',
        impactStudy: impactStudy,
        impactStudyDeny: impactStudyDeny
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          setFileProgress(undefined);
          showSuccess({
            title: i18n.t('end_flow.success_title'),
            message: i18n.t('npo_details.update_message')
          });
          router.back();
        },
        onError: (e) => {
          setIsLoading(false);
          setFileProgress(undefined);
          showError({ message: e.message });
          console.error('Error', e);
        }
      }
    );
  };

  const onSubmit: SubmitHandler<ImpactStudyData> = async (data) => {
    try {
      setIsLoading(true);
      if (data.impactStudy && !containsSection(data.impactStudy, 'impactStudy')) {
        let fileToUpload: FileToUpload = {
          uri: data.impactStudy,
          name: `impact_study.${new Date().toISOString()}`
        };
        await uploadToFireStorage(
          `non-profits-orgs/${authState.signUpData?.loginEmail?.npoId}/impactStudy/`,
          fileToUpload,
          (progress) => setFileProgress(progress)
        ).then((storageFile) => {
          updateNonProfit({
            impactStudy: [storageFile.metadata.fullPath],
            impactStudyDeny: data.impactStudyDeny
          });
        });
      } else {
        if (data.impactStudyDeny) {
          updateNonProfit({
            impactStudy: null,
            impactStudyDeny: data.impactStudyDeny
          });
        } else {
          updateNonProfit({
            impactStudy: [],
            impactStudyDeny: data.impactStudyDeny
          });
        }
      }
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      console.error(e);
    }
  };

  return (
    <NpoImpactStudyForm
      initialData={initialData}
      isLoading={nonProfitIsPending || isLoading}
      showBackButton={false}
      showSaveAndContinueButton={false}
      submitLabel={i18n.t('mix.done')}
      fileProgress={fileProgress}
      onSubmitTapped={onSubmit}
    />
  );
};

export default NpoImpactStudy;
